import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import './AdminPage.css';

const DEFAULT_SYSTEMS = [
  {
    id: 'financial',
    name: 'Sistema Financeiro',
    tag: 'Financeiro',
    badgeColor: '#10b981',
    badgeBg: '#d1fae5',
    icon: '💼',
    defaultUrl: window.location.origin,
    port: '3000',
    type: 'Principal (Web)',
    accessColumn: 'users.financial_active',
    middleware: 'CheckFinancialAccess',
    description: 'Gestão financeira empresarial completa: receitas, despesas, contas a pagar e a receber, conciliação bancária, cartões de crédito, relatórios e fluxo de caixa.',
    features: [
      'Contas a Pagar & Contas a Receber',
      'Conciliação Bancária & Cartões de Crédito',
      'Relatórios Gerenciais & Fluxo de Caixa',
      'Gestão de Clientes & Fornecedores',
      'Painel Administrativo & Gestão de Planos'
    ],
    isCurrent: true,
    target: '_self',
    appPath: '/dashboard'
  },
  {
    id: 'fleet',
    name: 'Gestão de Frotas',
    tag: 'Frotas',
    badgeColor: '#2563eb',
    badgeBg: '#dbeafe',
    icon: '🚛',
    defaultUrl: 'http://localhost:3002',
    port: '3002',
    type: 'Módulo Operacional',
    accessColumn: 'fleet_user_roles.is_active',
    middleware: 'CheckFleetRole',
    description: 'Sistema operacional de logística e frotas de transporte: controle de viagens, cargas, motoristas, manutenções preventivas, combustível, pedágios e fretes.',
    features: [
      'Gestão de Viagens, Cargas & Rotas',
      'Cadastro de Veículos & Motoristas',
      'Manutenções Preventivas & Oficinas',
      'Abastecimento, Combustível & Pedágios',
      'Checklist de Veículo & Comissões'
    ],
    isCurrent: false,
    target: '_blank',
    appPath: ''
  },
  {
    id: 'tasks',
    name: 'Gerenciador de Tarefas e Atividades',
    tag: 'Tarefas (Kanban)',
    badgeColor: '#8b5cf6',
    badgeBg: '#ede9fe',
    icon: '📋',
    defaultUrl: 'http://localhost:3004',
    port: '3004',
    type: 'Módulo Ágil',
    accessColumn: 'users.task_active',
    middleware: 'CheckTaskAccess',
    description: 'Gestão ágil de atividades no formato Kanban: quadros colaborativos, colunas dinâmicas, numeração sequencial de cards por cliente e permissões de equipe.',
    features: [
      'Quadros Kanban & Colunas Customizadas',
      'Numeração Sequencial de Cards por Cliente',
      'Membros por Quadro & Permissões Granulares',
      'Comentários, Anexos, Prazos & Prioridades',
      'Gestão Integrada de Usuários & Acessos'
    ],
    isCurrent: false,
    target: '_blank',
    appPath: ''
  }
];

export default function AdminSystemsPage() {
  const [urls, setUrls] = useState(() => {
    try {
      const saved = localStorage.getItem('systems_urls_config');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const [editingSystem, setEditingSystem] = useState(null);
  const [editUrl, setEditUrl] = useState('');
  const [checkingHealth, setCheckingHealth] = useState(false);
  const [healthStatus, setHealthStatus] = useState({});

  const getSystemUrl = (system) => {
    return urls[system.id] || system.defaultUrl;
  };

  const handleOpenEdit = (system) => {
    setEditingSystem(system);
    setEditUrl(getSystemUrl(system));
  };

  const handleSaveUrl = (e) => {
    e.preventDefault();
    if (!editingSystem) return;

    const newUrls = { ...urls, [editingSystem.id]: editUrl.trim() };
    setUrls(newUrls);
    localStorage.setItem('systems_urls_config', JSON.stringify(newUrls));
    setEditingSystem(null);

    Swal.fire({
      icon: 'success',
      title: 'URL atualizada com sucesso!',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 2000
    });
  };

  const handleResetUrl = (systemId) => {
    const newUrls = { ...urls };
    delete newUrls[systemId];
    setUrls(newUrls);
    localStorage.setItem('systems_urls_config', JSON.stringify(newUrls));

    Swal.fire({
      icon: 'info',
      title: 'URL restaurada para o padrão.',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 1500
    });
  };

  const checkAllHealth = async () => {
    setCheckingHealth(true);
    const newStatus = {};

    for (const sys of DEFAULT_SYSTEMS) {
      if (sys.isCurrent) {
        newStatus[sys.id] = 'online';
        continue;
      }
      const url = getSystemUrl(sys);
      try {
        // Ping fetch com timeout rápido
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);
        await fetch(url, { mode: 'no-cors', signal: controller.signal });
        clearTimeout(timeoutId);
        newStatus[sys.id] = 'online';
      } catch (err) {
        // Se abortou ou deu erro de conexão
        newStatus[sys.id] = 'checking_or_cors';
      }
    }

    setHealthStatus(newStatus);
    setCheckingHealth(false);
  };

  return (
    <div className="adm-systems-page">
      {/* Page Header */}
      <div className="adm-page-header">
        <div>
          <div className="adm-breadcrumb">
            <Link to="/admin">Dashboard</Link> / Sistemas
          </div>
          <h2>Sistemas Integrados</h2>
          <p className="text-muted" style={{ fontSize: '13px', margin: '4px 0 0' }}>
            Visão geral, URLs de acesso e arquitetura dos sistemas integrados da plataforma
          </p>
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            className="adm-btn adm-btn-secondary"
            onClick={checkAllHealth}
            disabled={checkingHealth}
          >
            {checkingHealth ? 'Verificando...' : '🔄 Testar Conexão'}
          </button>
          <Link
            to="/admin/systems/financial?tab=pagseguro"
            className="adm-btn adm-btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <span>⚙️</span> Credenciais PagSeguro (PIX)
          </Link>
        </div>
      </div>

      {/* KPI Overview */}
      <div className="adm-kpi-grid">
        <div className="adm-kpi-card">
          <div className="kpi-label">Sistemas Integrados</div>
          <div className="kpi-value">3</div>
          <div className="kpi-sub">Financeiro, Frotas e Tarefas</div>
        </div>

        <div className="adm-kpi-card">
          <div className="kpi-label">Base de Usuários</div>
          <div className="kpi-value">Unificada</div>
          <div className="kpi-sub">Tabela central de usuários</div>
        </div>

        <div className="adm-kpi-card">
          <div className="kpi-label">Controle de Acessos</div>
          <div className="kpi-value">Isolado</div>
          <div className="kpi-sub">Ativação individual por sistema</div>
        </div>

        <div className="adm-kpi-card">
          <div className="kpi-label">Status Operacional</div>
          <div className="kpi-value" style={{ color: '#059669' }}>100%</div>
          <div className="kpi-sub">Todos os serviços ativos</div>
        </div>
      </div>

      {/* PagSeguro Gateway Quick Access Banner */}
      <div
        style={{
          background: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: 10,
          padding: '14px 18px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: '24px', lineHeight: 1 }}>⚡</span>
          <div>
            <div style={{ fontWeight: 700, color: '#166534', fontSize: '14px' }}>
              Recebimento de Mensalidades via PIX (PagSeguro / PagBank)
            </div>
            <div style={{ fontSize: '13px', color: '#15803d', marginTop: 2 }}>
              Configure o Token de Produção ou Sandbox, Chave Webhook e teste a comunicação para emissão automática de QR Code PIX nos planos.
            </div>
          </div>
        </div>
        <Link
          to="/admin/systems/financial?tab=pagseguro"
          className="adm-btn adm-btn-sm"
          style={{ background: '#166534', color: '#fff', border: 'none', padding: '8px 16px', fontWeight: 600, whiteSpace: 'nowrap' }}
        >
          ⚙️ Configurar Credenciais PagSeguro &rarr;
        </Link>
      </div>

      {/* Architecture Explanatory Banner */}
      <div
        style={{
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: 10,
          padding: '14px 18px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
        }}
      >
        <span style={{ fontSize: '24px', lineHeight: 1 }}>ℹ️</span>
        <div style={{ fontSize: '13px', color: '#1e40af', lineHeight: 1.5 }}>
          <strong>Arquitetura Multi-Sistemas Integrada:</strong> Os três sistemas compartilham a mesma infraestrutura de banco de dados (MySQL/MariaDB) e autenticação.
          A desativação ou ativação de colaboradores em qualquer um dos módulos opera de maneira <strong>estritamente isolada</strong>, garantindo que o acesso a um sistema possa ser revogado sem interromper os demais.
        </div>
      </div>

      {/* Systems Cards Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px', marginBottom: '32px' }}>
        {DEFAULT_SYSTEMS.map((sys) => {
          const currentUrl = getSystemUrl(sys);
          const isCustom = Boolean(urls[sys.id]);
          const status = healthStatus[sys.id];

          return (
            <div
              key={sys.id}
              className="adm-card"
              style={{
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 12,
                border: sys.isCurrent ? '2px solid #6366f1' : '1px solid #e5e7eb',
                boxShadow: sys.isCurrent ? '0 4px 16px rgba(99, 102, 241, 0.12)' : '0 2px 6px rgba(0,0,0,0.04)',
                padding: '20px',
                position: 'relative',
              }}
            >
              {sys.isCurrent && (
                <div
                  style={{
                    position: 'absolute',
                    top: 12,
                    right: 14,
                    fontSize: '11px',
                    fontWeight: 700,
                    background: '#e0e7ff',
                    color: '#4338ca',
                    padding: '3px 8px',
                    borderRadius: 9999,
                  }}
                >
                  Sistema Atual
                </div>
              )}

              {/* Card Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '14px' }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    background: sys.badgeBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '22px',
                    flexShrink: 0,
                  }}
                >
                  {sys.icon}
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#111827' }}>
                    {sys.name}
                  </h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        color: sys.badgeColor,
                        background: sys.badgeBg,
                        padding: '2px 8px',
                        borderRadius: 9999,
                      }}
                    >
                      {sys.tag}
                    </span>
                    <span className="text-muted" style={{ fontSize: '11px' }}>
                      Porta {sys.port}
                    </span>
                    {status && (
                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: 600,
                          color: status === 'online' ? '#059669' : '#d97706',
                          background: status === 'online' ? '#d1fae5' : '#fef3c7',
                          padding: '2px 8px',
                          borderRadius: 9999,
                        }}
                      >
                        {status === 'online' ? '● Online' : '○ Conectando'}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              <p style={{ fontSize: '13px', color: '#4b5563', lineHeight: 1.5, marginBottom: '14px' }}>
                {sys.description}
              </p>

              {/* Features List */}
              <div style={{ background: '#f9fafb', borderRadius: 8, padding: '12px', marginBottom: '16px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', marginBottom: '8px', letterSpacing: '0.04em' }}>
                  Principais Recursos
                </div>
                <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12px', color: '#374151', lineHeight: 1.6 }}>
                  {sys.features.map((feat, idx) => (
                    <li key={idx}>{feat}</li>
                  ))}
                </ul>
              </div>

              {/* Technical / Access Details */}
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div>
                  <strong>Controle de Acesso:</strong> <code style={{ fontSize: '11px', background: '#f3f4f6', padding: '1px 5px', borderRadius: 4 }}>{sys.accessColumn}</code>
                </div>
                <div>
                  <strong>Middleware:</strong> <code style={{ fontSize: '11px', background: '#f3f4f6', padding: '1px 5px', borderRadius: 4 }}>{sys.middleware}</code>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <strong>URL:</strong>{' '}
                    <a href={currentUrl} target={sys.target} rel="noreferrer" style={{ color: '#4f46e5', textDecoration: 'none' }}>
                      {currentUrl}
                    </a>
                  </span>
                  <button
                    type="button"
                    onClick={() => handleOpenEdit(sys)}
                    style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '11px', padding: 0, textDecoration: 'underline' }}
                  >
                    Editar URL
                  </button>
                </div>
                {isCustom && (
                  <div style={{ fontSize: '11px', color: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>⚠️ URL personalizada ativa</span>
                    <button
                      type="button"
                      onClick={() => handleResetUrl(sys.id)}
                      style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '11px', padding: 0, textDecoration: 'underline' }}
                    >
                      Restaurar padrão
                    </button>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div style={{ marginTop: 'auto', paddingTop: '12px', borderTop: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Link
                  to={`/admin/systems/${sys.id}`}
                  className="adm-btn adm-btn-primary"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    textAlign: 'center',
                    textDecoration: 'none',
                    width: '100%',
                    fontWeight: 600,
                  }}
                >
                  ⚙️ Gerenciar Planos, Clientes e Cobranças
                </Link>

                {sys.isCurrent ? (
                  <Link
                    to={sys.appPath}
                    className="adm-btn adm-btn-secondary"
                    style={{ display: 'block', textAlign: 'center', textDecoration: 'none', width: '100%' }}
                  >
                    Ir para Dashboard do Financeiro
                  </Link>
                ) : (
                  <a
                    href={currentUrl}
                    target={sys.target}
                    rel="noreferrer"
                    className="adm-btn adm-btn-secondary"
                    style={{ display: 'block', textAlign: 'center', textDecoration: 'none', width: '100%' }}
                  >
                    Acessar {sys.name} ↗
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Comparison & Access Control Table */}
      <div className="adm-card">
        <h3>Matriz de Isolamento de Acessos por Sistema</h3>
        <p className="text-muted" style={{ fontSize: '13px', marginBottom: '16px' }}>
          Configuração de permissões e controle de status de usuários em cada aplicação do ecossistema:
        </p>

        <div className="adm-table-wrapper">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Sistema</th>
                <th>Porta / Ambiente</th>
                <th>Coluna de Ativação</th>
                <th>Middleware de Proteção</th>
                <th>Impacto ao Desativar</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>💼 Sistema Financeiro</strong></td>
                <td>Porta 3000</td>
                <td><code>users.financial_active</code></td>
                <td><code>CheckFinancialAccess</code></td>
                <td>Bloqueia apenas o módulo Financeiro</td>
                <td><span className="adm-badge adm-badge-active">Ativo</span></td>
              </tr>
              <tr>
                <td><strong>🚛 Gestão de Frotas</strong></td>
                <td>Porta 3002</td>
                <td><code>fleet_user_roles.is_active</code></td>
                <td><code>CheckFleetRole</code></td>
                <td>Bloqueia apenas o módulo de Frotas</td>
                <td><span className="adm-badge adm-badge-active">Ativo</span></td>
              </tr>
              <tr>
                <td><strong>📋 Gerenciador de Tarefas</strong></td>
                <td>Porta 3004</td>
                <td><code>users.task_active</code></td>
                <td><code>CheckTaskAccess</code></td>
                <td>Bloqueia apenas o Gerenciador de Tarefas</td>
                <td><span className="adm-badge adm-badge-active">Ativo</span></td>
              </tr>
              <tr style={{ background: '#f8fafc' }}>
                <td><strong>🔐 Conta Geral (Mestre)</strong></td>
                <td>API Backend (8000)</td>
                <td><code>users.is_active</code></td>
                <td><code>auth:api</code> / Base Auth</td>
                <td>Bloqueia login em <em>todos</em> os sistemas simultaneamente</td>
                <td><span className="adm-badge adm-badge-active">Global</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal: Editar URL do Sistema */}
      {editingSystem && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1050,
            padding: '16px',
          }}
          onClick={() => setEditingSystem(null)}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '12px',
              maxWidth: '480px',
              width: '100%',
              padding: '24px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>
                Configurar URL: {editingSystem.name}
              </h4>
              <button
                type="button"
                className="btn-close"
                onClick={() => setEditingSystem(null)}
              ></button>
            </div>

            <form onSubmit={handleSaveUrl}>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: '13px', marginBottom: '4px' }}>
                  URL de Acesso ao Sistema
                </label>
                <input
                  type="url"
                  className="form-control"
                  value={editUrl}
                  onChange={(e) => setEditUrl(e.target.value)}
                  placeholder="http://localhost:3000"
                  required
                  autoFocus
                />
                <small className="text-muted" style={{ fontSize: '11px', display: 'block', marginTop: '4px' }}>
                  Padrão: <code>{editingSystem.defaultUrl}</code>
                </small>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
                <button
                  type="button"
                  className="adm-btn adm-btn-secondary"
                  onClick={() => setEditingSystem(null)}
                >
                  Cancelar
                </button>
                <button type="submit" className="adm-btn adm-btn-primary">
                  Salvar URL
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
